/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Cup Detection Test - Debug tool
 * Created: 2026-01-09
 * 
 * Test detection algorithm with specific files:
 * - testing/clean/ly2.jpg (mask with red box)
 * - testing/clean/ly2-no-red-color.jpg (clean image)
 */

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  area?: number;
  pixelCount?: number;
}

/**
 * Load image from path
 */
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
};

/**
 * Detect red boxes (STRICT threshold)
 */
const detectRedBoxes = (canvas: HTMLCanvasElement): BoundingBox[] => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return [];

  const width = canvas.width;
  const height = canvas.height;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const visited = new Uint8Array(width * height);
  const boxes: BoundingBox[] = [];

  const isRed = (idx: number) => {
    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];
    return r > 120 && g < 100 && b < 100;
  };

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x);
      const pixelIndex = index * 4;

      if (visited[index]) continue;

      if (isRed(pixelIndex)) {
        let minX = x, maxX = x, minY = y, maxY = y;
        const stack = [index];
        visited[index] = 1;
        let count = 0;

        while (stack.length > 0) {
          const currIdx = stack.pop()!;
          const currX = currIdx % width;
          const currY = Math.floor(currIdx / width);

          count++;
          if (currX < minX) minX = currX;
          if (currX > maxX) maxX = currX;
          if (currY < minY) minY = currY;
          if (currY > maxY) maxY = currY;

          const neighbors = [currIdx - width, currIdx + width, currIdx - 1, currIdx + 1];

          for (const n of neighbors) {
            if (n < 0 || n >= width * height) continue;
            if (Math.abs((n % width) - currX) > 1) continue;

            if (!visited[n] && isRed(n * 4)) {
              visited[n] = 1;
              stack.push(n);
            }
          }
        }

        if (count > 200 && (maxX - minX) > 10 && (maxY - minY) > 10) {
          boxes.push({
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            area: (maxX - minX) * (maxY - minY),
            pixelCount: count
          });
        }
      }
    }
  }

  return boxes;
};

/**
 * Test detection with ly2.jpg files
 */
export const testCupDetection = async () => {
  console.log('🧪 [TEST] Starting cup detection test...');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  const maskPath = '/testing/clean/ly2.jpg';
  const cleanPath = '/testing/clean/ly2-no-red-color.jpg';

  try {
    // 1. Load images
    console.log('📂 [TEST] Loading images...');
    const [maskImg, cleanImg] = await Promise.all([
      loadImage(maskPath),
      loadImage(cleanPath)
    ]);

    console.log(`📐 [TEST] Mask image size: ${maskImg.width}x${maskImg.height}`);
    console.log(`📐 [TEST] Clean image size: ${cleanImg.width}x${cleanImg.height}`);

    // Check if sizes match
    if (maskImg.width !== cleanImg.width || maskImg.height !== cleanImg.height) {
      console.warn('⚠️  [TEST] WARNING: Mask and clean images have different sizes!');
      console.warn(`   Mask: ${maskImg.width}x${maskImg.height}`);
      console.warn(`   Clean: ${cleanImg.width}x${cleanImg.height}`);
    }

    // 2. Test detection WITHOUT resize (original size)
    console.log('\n🔍 [TEST] TEST 1: Detection at ORIGINAL size');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    const canvas1 = document.createElement('canvas');
    canvas1.width = maskImg.width;
    canvas1.height = maskImg.height;
    const ctx1 = canvas1.getContext('2d');
    if (ctx1) {
      ctx1.drawImage(maskImg, 0, 0);
      const boxes1 = detectRedBoxes(canvas1);
      console.log(`   Found ${boxes1.length} boxes:`);
      boxes1.forEach((box, i) => {
        console.log(`   Box ${i + 1}: x=${box.x}, y=${box.y}, w=${box.width}, h=${box.height}, area=${box.area}, pixels=${box.pixelCount}`);
        console.log(`   → Center: (${box.x + box.width/2}, ${box.y + box.height/2})`);
        console.log(`   → Relative position: ${((box.x / maskImg.width) * 100).toFixed(1)}% from left, ${((box.y / maskImg.height) * 100).toFixed(1)}% from top`);
      });
    }

    // 3. Test detection WITH resize to 1024px (like cupMockupService)
    console.log('\n🔍 [TEST] TEST 2: Detection at 1024px (scaled)');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    const maxSize = 1024;
    let w = maskImg.width;
    let h = maskImg.height;
    const scaleRatio = Math.min(maxSize / w, maxSize / h);
    const scaledW = Math.floor(w * scaleRatio);
    const scaledH = Math.floor(h * scaleRatio);

    console.log(`   Scale ratio: ${scaleRatio.toFixed(4)}`);
    console.log(`   Scaled size: ${scaledW}x${scaledH}`);

    const canvas2 = document.createElement('canvas');
    canvas2.width = scaledW;
    canvas2.height = scaledH;
    const ctx2 = canvas2.getContext('2d');
    if (ctx2) {
      ctx2.drawImage(maskImg, 0, 0, scaledW, scaledH);
      const boxes2 = detectRedBoxes(canvas2);
      console.log(`   Found ${boxes2.length} boxes:`);
      boxes2.forEach((box, i) => {
        console.log(`   Box ${i + 1}: x=${box.x}, y=${box.y}, w=${box.width}, h=${box.height}, area=${box.area}, pixels=${box.pixelCount}`);
        console.log(`   → Center: (${box.x + box.width/2}, ${box.y + box.height/2})`);
        console.log(`   → Relative position: ${((box.x / scaledW) * 100).toFixed(1)}% from left, ${((box.y / scaledH) * 100).toFixed(1)}% from top`);
        
        // Scale back to original coordinates
        const origX = Math.floor(box.x / scaleRatio);
        const origY = Math.floor(box.y / scaleRatio);
        const origW = Math.floor(box.width / scaleRatio);
        const origH = Math.floor(box.height / scaleRatio);
        console.log(`   → Original coords: x=${origX}, y=${origY}, w=${origW}, h=${origH}`);
      });
    }

    // 4. Visual debug: Draw detected boxes on canvas
    console.log('\n🖼️  [TEST] TEST 3: Visual debug (check browser)');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    const debugCanvas = document.createElement('canvas');
    debugCanvas.width = maskImg.width;
    debugCanvas.height = maskImg.height;
    debugCanvas.style.border = '2px solid red';
    debugCanvas.style.maxWidth = '100%';
    const debugCtx = debugCanvas.getContext('2d');
    
    if (debugCtx) {
      // Draw mask image
      debugCtx.drawImage(maskImg, 0, 0);
      
      // Draw detected boxes in green
      const boxes = detectRedBoxes(canvas1);
      debugCtx.strokeStyle = 'lime';
      debugCtx.lineWidth = 3;
      boxes.forEach((box, i) => {
        debugCtx.strokeRect(box.x, box.y, box.width, box.height);
        
        // Draw label
        debugCtx.fillStyle = 'lime';
        debugCtx.font = 'bold 16px monospace';
        debugCtx.fillText(`Detected Box ${i + 1}`, box.x + 5, box.y - 5);
        
        // Draw center point
        const cx = box.x + box.width / 2;
        const cy = box.y + box.height / 2;
        debugCtx.fillStyle = 'red';
        debugCtx.beginPath();
        debugCtx.arc(cx, cy, 5, 0, Math.PI * 2);
        debugCtx.fill();
      });
      
      // Append to body for visual inspection
      document.body.appendChild(debugCanvas);
      console.log('   ✓ Debug canvas added to page (scroll down to see)');
    }

    console.log('\n✅ [TEST] Test complete!');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

  } catch (err) {
    console.error('❌ [TEST] Test failed:', err);
  }
};

// Export for use in console
(window as any).testCupDetection = testCupDetection;

console.log('🧪 Cup Detection Test loaded. Run `testCupDetection()` in console to test.');
